import React, { useCallback } from 'react';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import { Footer, TitleBar } from '../../../../components';
import QueryErrorBoundary from '../../../../components/error-handling';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import { useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router';
import { GET_LOCATION_NAME } from '../../../../graphql/utils/queries';
import { Location } from '../../../../interfaces/RiskHub';
import { TableColumnsType } from 'antd';
import { tableSortingMethods } from '../../../../utils';
import FinancialData from '../../../../components/common/FinancialData';
import PercentageFormatter from '../../../../components/common/PercentageFormatter';
import { GET_LOCATION_IT_APPS_SUMMARY } from './queries';
import { UPDATE_IT_APPLICATION } from '../../../dataInputs/queries/itApplications';
import { CollapsibleText } from '../../../../components/generic/TextTruncation';

type FetchedLocation = {
  findLocationByUuid: Location;
};

type ITApplicationSummary = {
  uuid: string;
  name: string;
  applicationDescription: string;
  percentageOfCompanySupported: number;
  revenueSupportedAnnualy: number;
};

type ITAppSummaryFetchedType = {
  getLocationItApplicationsSummary: ITApplicationSummary[];
};

const LocationApplicationSummary = () => {
  const { locationId } = useParams();

  const { data: locationNameData, error: locationNameError } =
    useQuery<FetchedLocation>(GET_LOCATION_NAME, {
      variables: {
        uuid: locationId,
      },
      skip: !locationId,
    });

  const locationName = locationNameData?.findLocationByUuid.locationName ?? '';

  const {
    data: locationITAppData,
    loading: locationITAppLoading,
    error: locationITAppError,
  } = useQuery<ITAppSummaryFetchedType>(GET_LOCATION_IT_APPS_SUMMARY, {
    variables: {
      data: locationId,
    },
    skip: !locationId,
  });

  const ITAppsSummary =
    locationITAppData?.getLocationItApplicationsSummary ?? [];

  const [updateITApplication] = useMutation(UPDATE_IT_APPLICATION, {
    refetchQueries: [
      'FindAllItApplications',
      'GetLocationItApplicationsSummary',
    ],
  });

  const columns: TableColumnsType<ITApplicationSummary> = [
    {
      title: 'Applications',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (applicationName: string) => (
        <div className="flex flex-col">
          <span className="custom-table--column ml-5">{applicationName}</span>
        </div>
      ),
    },
    {
      title: (
        <span>
          Revenue Supported <br />
          Annually
        </span>
      ),
      dataIndex: 'revenueSupportedAnnualy',
      key: 'revenueSupportedAnnualy',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.revenueSupportedAnnualy,
          b.revenueSupportedAnnualy,
        ),
      render: (revenue: number) => (
        <span className="custom-table--column">
          <FinancialData text={revenue} />
        </span>
      ),
    },
    {
      title: (
        <span>
          % of Location <br /> Supported
        </span>
      ),
      dataIndex: 'percentageOfCompanySupported',
      key: 'percentageOfCompanySupported',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.percentageOfCompanySupported,
          b.percentageOfCompanySupported,
        ),
      render: (percentage: number) => (
        <span className="custom-table--column">
          <PercentageFormatter value={percentage} />
        </span>
      ),
    },
    {
      title: 'Application Description',
      dataIndex: 'applicationDescription',
      key: 'applicationDescription',
      width: 200,
      align: 'left',
      render: (text: string) => <CollapsibleText text={text} />,
    },
  ];

  const formFields = [
    {
      label: 'Application Description',
      key: 'applicationDescription',
      type: 'textArea',
    },
  ];

  const modifyVariables = useCallback(({ values }: ModifyVariablesArgs) => {
    return {
      description: values.applicationDescription,
    };
  }, []);

  return (
    <>
      <TitleUpdater title="Location Application Dependency Mapping" />
      <QueryErrorBoundary error={locationNameError}>
        <TitleBar
          title="Location Application Dependency Mapping"
          subtitle={`${locationName} Application Summary`}
        />
        <QueryErrorBoundary error={locationITAppError}>
          <TableV3
            columns={columns}
            data={ITAppsSummary}
            loading={locationITAppLoading}
            updateGQL={updateITApplication}
            editable
            editableOptions={{
              update: true,
            }}
            inlineEditing
            formFields={formFields}
            modifyVariables={modifyVariables}
          />
        </QueryErrorBoundary>
        <Footer
          backPath={`/cyber/location-level/${locationId}/it-applications/mapping`}
        />
      </QueryErrorBoundary>
    </>
  );
};

export default LocationApplicationSummary;
